import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { withTheme } from "../../../theming";
import { useResponsive } from "../../../contexts/ResponsiveContext";

const Header = ({ isAuthenticated, setIsAuthenticated, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const navigate = useNavigate();
  const moreDropdownRef = useRef(null);
  const {
    isMobile,
    isTablet,
    isDesktop,
    isPhoneLandscape,
    shouldShowHamburgerMenu,
  } = useResponsive();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        moreDropdownRef.current &&
        !moreDropdownRef.current.contains(event.target)
      ) {
        setIsMoreOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    // Handle logout logic here
    localStorage.removeItem("jwtToken");
    setIsAuthenticated(false);
    navigate("/login");
    // Close the menu after logout
    setIsOpen(false);
  };

  // Function to handle menu item click
  const handleMenuItemClick = () => {
    setIsOpen(false);
    setIsMoreOpen(false);
  };

  // Primary navigation items (always visible)
  const primaryMenuLinks = [
    { name: "AI Agents", path: "/ai-agents" },
    { name: "AI in Business", path: "/ai-agents-business" },
    { name: "Agent Architecture", path: "/agent-architecture-types" },
    { name: "Interaction Patterns", path: "/agent-interaction-patterns" },
    { name: "Infrastructure", path: "/infrastructure" },
    { name: "API Keys & Tools", path: "/api-key-registration" },
    { name: "AI Agent Config", path: "/ai-agent-configuration" },
    { name: "Solutions", path: "/solutions" },
    { name: "Platform", path: "/platform" },
    { name: "Pricing", path: "/pricing" },
  ];

  // Secondary navigation items (in "More" dropdown)
  const secondaryMenuLinks = [
    { name: "No-Code vs Enterprise", path: "/ai-agents-comparison" },
    { name: "AI Automation Studio", path: "/ai-automation-studio" },
    { name: "Multi-Agent", path: "/multi-agent-architectures" },
    { name: "RAG Systems", path: "/rag-systems-architecture" },
    { name: "Startups", path: "/startups" },
    { name: "Your Journey", path: "/fipsr-ai-journey" },
    { name: "Founder Profile", path: "/founder-profile" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <header
      style={{
        backgroundColor: theme.primaryColor || "#009688",
        color: theme.buttonTextColor || "#FFF",
      }}
      className="p-4"
    >
      <div className="mx-auto flex justify-between items-center pr-10 pl-5">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <NavLink
            to="/"
            className="text-[145%] font-bold hover:opacity-90 focus:opacity-90 transition-opacity duration-200"
            style={{
              color: theme.buttonTextColor || "#FFFFFF",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
            }}
          >
            Fipsr Agentics
          </NavLink>
        </div>

        {/* Hamburger Menu Icon */}
        <div style={{ display: shouldShowHamburgerMenu ? "block" : "none" }}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none"
            style={{
              backgroundColor: theme.primaryColor || "#009688",
              color: theme.secondaryColor || "#FFF",
            }}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
        </div>

        {/* Desktop/Tablet Menu */}
        <nav
          style={{
            display: shouldShowHamburgerMenu ? "none" : "flex",
            gap: isTablet ? "8px" : "16px",
          }}
          className="items-center"
        >
          {/* Primary Menu Items */}
          {primaryMenuLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                isActive
                  ? `font-semibold ${
                      isTablet ? "text-[100%]" : "text-[110%]"
                    } px-2 py-2 rounded-md transition-all duration-200 no-underline outline-none border-none whitespace-nowrap`
                  : `${
                      isTablet ? "text-[100%]" : "text-[110%]"
                    } px-2 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-20 no-underline outline-none border-none whitespace-nowrap`
              }
              style={({ isActive }) => ({
                color: theme.buttonTextColor || "#FFFFFF",
                backgroundColor: isActive
                  ? "rgba(255, 255, 255, 0.2)"
                  : "transparent",
                textDecoration: "none",
                outline: "none",
                border: "none",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
              })}
            >
              {link.name}
            </NavLink>
          ))}

          {/* More Dropdown */}
          <div className="relative" ref={moreDropdownRef}>
            <button
              onClick={() => setIsMoreOpen(!isMoreOpen)}
              className="text-[110%] px-2 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-20 whitespace-nowrap"
              style={{
                color: theme.buttonTextColor || "#FFFFFF",
                backgroundColor: isMoreOpen
                  ? "rgba(255, 255, 255, 0.2)"
                  : "transparent",
                textDecoration: "none",
                outline: "none",
                border: "none",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
              }}
            >
              More ▼
            </button>

            {/* Dropdown Menu */}
            {isMoreOpen && (
              <div className="absolute top-full right-0 mt-1 bg-white rounded-md shadow-lg py-2 min-w-[180px] z-50">
                {secondaryMenuLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    onClick={handleMenuItemClick}
                    className="block px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors duration-200 no-underline"
                    style={{
                      textDecoration: "none",
                      outline: "none",
                      border: "none",
                    }}
                  >
                    {link.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
          {/* {isAuthenticated ? (
            <NavLink
              to="#"
              onClick={handleLogout}
              className="text-[120%] px-3 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-20 no-underline outline-none border-none"
              style={{
                color: theme.buttonTextColor || "#FFFFFF",
                textDecoration: "none",
                outline: "none",
                border: "none",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
              }}
            >
              Logout
            </NavLink>
          ) : (
            <NavLink
              to="/login"
              className="text-[120%] px-3 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-20 no-underline outline-none border-none"
              style={{
                color: theme.buttonTextColor || "#FFFFFF",
                textDecoration: "none",
                outline: "none",
                border: "none",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
              }}
            >
              Login
            </NavLink>
          )} */}
        </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && shouldShowHamburgerMenu && (
        <nav className="mt-4 bg-black bg-opacity-10 rounded-lg p-2">
          {/* Primary Menu Items */}
          {primaryMenuLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              onClick={handleMenuItemClick}
              className="block py-3 px-4 text-[120%] rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-20 no-underline outline-none border-none"
              style={{
                color: theme.buttonTextColor || "#FFFFFF",
                textDecoration: "none",
                outline: "none",
                border: "none",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
              }}
            >
              {link.name}
            </NavLink>
          ))}

          {/* Secondary Menu Items */}
          {secondaryMenuLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              onClick={handleMenuItemClick}
              className="block py-3 px-4 text-[120%] rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-20 no-underline outline-none border-none"
              style={{
                color: theme.buttonTextColor || "#FFFFFF",
                textDecoration: "none",
                outline: "none",
                border: "none",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
              }}
            >
              {link.name}
            </NavLink>
          ))}

          {/* {isAuthenticated ? (
            <NavLink
              to="#"
              onClick={handleLogout}
              className="block py-3 px-4 text-[120%] rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-20 no-underline outline-none border-none"
              style={{
                color: theme.buttonTextColor || "#FFFFFF",
                textDecoration: "none",
                outline: "none",
                border: "none",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
              }}
            >
              Logout
            </NavLink>
          ) : (
            <NavLink
              to="/login"
              onClick={handleMenuItemClick}
              className="block py-3 px-4 text-[120%] rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-20 no-underline outline-none border-none"
              style={{
                color: theme.buttonTextColor || "#FFFFFF",
                textDecoration: "none",
                outline: "none",
                border: "none",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
              }}
            >
              Login
            </NavLink>
          )} */}
        </nav>
      )}
    </header>
  );
};

export default withTheme(Header);
